import React, { useEffect, useState } from 'react';
import { Share, PlusSquare, Download, Smartphone, X, Check } from 'lucide-react';
import { toast } from 'sonner';
import { canPromptInstall, onInstallPromptChange, promptInstall, isIOS } from '../../services/installPrompt';

export const INSTALL_GUIDE_SEEN_KEY = 'obgh_install_guide_seen_v1';

const Step = ({ n, icon: Icon, children }) => (
  <li className="flex items-start gap-4">
    <span className="flex-shrink-0 w-8 h-8 rounded-sm bg-[#F97316] text-zinc-950 font-black flex items-center justify-center font-heading">{n}</span>
    <div className="flex items-center gap-2 text-sm text-zinc-200 leading-relaxed pt-1">
      <Icon size={18} className="text-[#F97316] flex-shrink-0" />
      <span>{children}</span>
    </div>
  </li>
);

export const InstallGuide = ({ open, onClose }) => {
  const [canInstall, setCanInstall] = useState(canPromptInstall());
  const ios = isIOS();

  useEffect(() => onInstallPromptChange(setCanInstall), []);

  const dismiss = () => {
    try { localStorage.setItem(INSTALL_GUIDE_SEEN_KEY, '1'); } catch { /* ignore */ }
    onClose();
  };

  const install = async () => {
    const outcome = await promptInstall();
    if (outcome === 'accepted') { toast.success('OBGH installé — ouvre-le depuis ton écran d\'accueil'); dismiss(); }
    else if (outcome === 'unavailable') toast.info('Utilise le menu ⋮ de ton navigateur → « Installer l\'application »');
  };

  if (!open) return null;

  return (
    <div
      data-testid="install-guide"
      className="fixed inset-0 z-[90] bg-[#09090B]/[0.98] backdrop-blur-xl flex flex-col text-zinc-100 overflow-y-auto pt-[calc(env(safe-area-inset-top)+16px)] pb-[calc(env(safe-area-inset-bottom)+16px)] px-6 animate-in fade-in duration-300"
    >
      <button onClick={dismiss} data-testid="install-guide-close" aria-label="Fermer" className="self-end p-2 text-zinc-400 hover:text-white">
        <X size={22} />
      </button>

      <div className="flex-1 flex flex-col justify-center max-w-md w-full mx-auto">
        <img src="/obgh-logo-v2.png" alt="OBGH" className="w-20 h-20 object-contain mb-6" />
        <p className="text-xs uppercase tracking-[0.2em] font-bold text-orange-500 mb-2">Vraie app · Plein écran · Hors-ligne</p>
        <h1 className="font-heading font-black uppercase tracking-tight text-3xl sm:text-4xl leading-none mb-3">
          Installe OBGH sur ton écran d'accueil
        </h1>
        <p className="text-sm text-zinc-400 leading-relaxed mb-8">
          Sans barre de navigateur, avec ton icône, démarrage instantané même sans signal.
        </p>

        {ios ? (
          <ol className="space-y-4 mb-8" data-testid="install-steps-ios">
            <Step n={1} icon={Share}>Touche le bouton <strong>Partager</strong> (carré avec flèche vers le haut) en bas de Safari.</Step>
            <Step n={2} icon={PlusSquare}>Défile et choisis <strong>« Sur l'écran d'accueil »</strong>.</Step>
            <Step n={3} icon={Check}>Touche <strong>Ajouter</strong> en haut à droite. Lance ensuite OBGH depuis ton icône.</Step>
          </ol>
        ) : (
          <div className="mb-8" data-testid="install-steps-android">
            <button
              onClick={install}
              data-testid="install-app-btn"
              className="w-full bg-[#F97316] hover:bg-orange-600 text-zinc-950 font-black uppercase tracking-wider py-4 px-6 flex items-center justify-center gap-2 text-base transition-colors active:scale-[0.99]"
            >
              <Download size={20} /> {canInstall ? 'Installer l\'application' : 'Installer via le menu ⋮'}
            </button>
            {!canInstall && (
              <p className="text-xs text-zinc-500 mt-3 flex items-start gap-2">
                <Smartphone size={14} className="flex-shrink-0 mt-0.5" />
                Chrome / Samsung Internet : menu ⋮ en haut à droite → « Installer l'application » ou « Ajouter à l'écran d'accueil ».
              </p>
            )}
          </div>
        )}

        <button onClick={dismiss} data-testid="install-guide-skip" className="text-zinc-400 text-xs font-mono uppercase tracking-widest hover:text-zinc-200 text-center py-3">
          Continuer dans le navigateur
        </button>
      </div>
    </div>
  );
};
